/**
 * Haravan Cart Service
 * Add products to cart with custom properties (e.g., design_id)
 * 
 * Based on: modal_backend/integration/app-test/test_add_cart.html
 * Method: JavaScript Dynamic Form (POST to custom.airxcoffee.com/cart/add)
 */

interface AddToCartParams {
  variantId: number;          // Haravan variant ID
  designId: string;           // Design ID from Firestore
  quantity?: number;          // Quantity (default: 1)
  customProperties?: Record<string, string>; // Additional custom properties
}

/**
 * Add product to Haravan cart with design_id
 * Uses Form POST method (only reliable method for cross-origin cart operations)
 * 
 * IMPORTANT: AJAX methods don't work due to CORS/cookie restrictions between domains
 * - AJAX from veritas-image.web.app → custom.airxcoffee.com returns 200 but doesn't persist cart
 * - Form POST redirects to custom.airxcoffee.com, so cookies work correctly
 * 
 * @param params - Cart parameters
 */
export const addToHaravanCart = (params: AddToCartParams): void => {
  const {
    variantId,
    designId,
    quantity = 1,
    customProperties = {}
  } = params;

  // Get Haravan store URL from env
  const haravanStoreUrl = import.meta.env.VITE_HARAVAN_STORE_URL || 'https://custom.airxcoffee.com';

  console.log('🛒 [Haravan] Adding to cart via Form POST:', {
    variantId,
    designId,
    quantity,
    customProperties
  });

  // Create hidden form
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = `${haravanStoreUrl}/cart/add`;
  form.style.display = 'none';

  // Add variant ID
  const variantInput = document.createElement('input');
  variantInput.type = 'hidden';
  variantInput.name = 'id';
  variantInput.value = variantId.toString();
  form.appendChild(variantInput);

  // Add quantity
  const qtyInput = document.createElement('input');
  qtyInput.type = 'hidden';
  qtyInput.name = 'quantity';
  qtyInput.value = quantity.toString();
  form.appendChild(qtyInput);

  // Add Design ID as custom property
  const designInput = document.createElement('input');
  designInput.type = 'hidden';
  designInput.name = 'properties[DesignID]';
  designInput.value = designId;
  form.appendChild(designInput);

  // Add any additional custom properties
  Object.entries(customProperties).forEach(([key, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = `properties[${key}]`;
    input.value = value;
    form.appendChild(input);
  });

  // Log form data
  console.log('📤 [Haravan] Form data:');
  console.log('  - id:', variantId);
  console.log('  - quantity:', quantity);
  console.log('  - properties[DesignID]:', designId);
  console.log('  - Action:', form.action);

  // Append to body and submit (will redirect to cart page)
  document.body.appendChild(form);
  console.log('🚀 [Haravan] Submitting form (will redirect)...');
  form.submit();
};

/**
 * Generate Haravan cart URL with design_id (deprecated, use addToHaravanCart instead)
 * This method has issues with redirects from myharavan.com to custom domain
 * 
 * @deprecated Use addToHaravanCart() instead
 */
export const generateHaravanCartUrl = (variantId: number, designId: string): string => {
  const haravanStoreUrl = import.meta.env.VITE_HARAVAN_STORE_URL || 'https://custom.airxcoffee.com';
  return `${haravanStoreUrl}/cart/${variantId}:1?properties[DesignID]=${encodeURIComponent(designId)}`;
};
